import { call, takeLatest, fork, all, put, select } from 'redux-saga/effects';
import { get } from 'lodash';

import fileSaver from 'file-saver';
import { selectUser } from 'modules/user';
import { getConfig } from 'modules/config';
import { showNotification } from 'modules/snackbar';

import { helpers } from 'veritone-redux-common';
const { fetchGraphQLApi } = helpers;

import * as builds from './';
import { addEngineFlow } from './workflow';

function* downloadBuildReport(report) {
  const reportJson = JSON.stringify(report, null, '\t');

  yield call(
    fileSaver.saveAs,
    new Blob([reportJson], { type: 'text/plain;charset=utf-8' }),
    `build-${report.build}.json`
  );
}

function* buildNodeRedEngine(action) {
  const { engineId } = action.payload;
  const user = yield select(selectUser);
  const { organizationId } = user.organization;
  yield put(
    builds.fetchWorkflowRuntimeData({
      engineId,
      organizationId,
    })
  );
}

function* updateWorkflowData(action) {
  const { engineId, organizationId } = action.meta;
  const workflowDatas = get(
    action,
    'payload.data.workflowRuntimeStorageData.records',
    []
  );
  const nodeRedFlow = workflowDatas.find(
    (workflow) => workflow.storageKey === 'flows'
  );
  const storageData = JSON.parse(nodeRedFlow.storageData);
  // append sample engine flow
  const updatedFlowData = addEngineFlow(storageData, engineId);
  const engineFlow = {
    ...nodeRedFlow,
    storageData: JSON.stringify(updatedFlowData),
  };
  yield put(
    builds.updateWorkflowData({
      organizationId,
      engineFlow,
    })
  );
}

function* openWorkflowApp() {
  const config = yield select(getConfig);
  const { workflowPageUri } = config.pageUris;
  window.open(workflowPageUri, '_blank');
}

function* showNotificationError() {
  const message = 'Something went wrong; please try again later.';
  yield put(showNotification(message));
}

function* redirectToLatestAutomateBuild(action) {
  const engineId = action.payload.engineId;
  const token = action.payload.token;
  const endpoint = `${window.config.apiRoot}/graphql`;
  const automatePageUri = get(
    window.config,
    'pageUris.automatePageUri',
    'https://automate.veritone.com'
  );
  try {
    const query = builds.getEngineBuildsQuery;
    const getEngineBuildsResponse = yield fetchGraphQLApi({
      endpoint,
      query,
      variables: { engineId },
      operationName: null,
      token,
    });

    const latestEngineBuild = get(
      getEngineBuildsResponse,
      'data.engine.builds.records[0].id'
    );
    if (!latestEngineBuild) {
      throw new Error('No engine build id found');
    }
    window.open(
      `${automatePageUri}/flow/${engineId}/${latestEngineBuild}`,
      '_blank',
      'noopener'
    );
  } catch (err1) {
    // No engine build was found, the first one is created here.
    try {
      const engine = yield getEngine(token, endpoint, engineId);
      const engineMode = get(engine, 'manifest.engineMode', 'chunk');
      const query = builds.createEngineBuildAndUpdateEngineJWTRightsMutation;
      const dockerImage = get(window.config, 'automate.controllerNodeRedImage');
      const manifest = { runtime: 'nodeRed', engineId, engineMode };
      const taskRuntime = {
        edge: {},
        nodeRed: {
          flows: [],
        },
      };
      const JWTRights = builds.automateEngineJWTRights;
      if (!dockerImage) {
        throw new Error('Missing docker image url');
      }
      const createEngineBuildAndUpdateEngineJWTRightsResponse =
        yield fetchGraphQLApi({
          endpoint,
          query,
          variables: {
            engineId,
            manifest,
            taskRuntime,
            dockerImage,
            JWTRights,
          },
          operationName: null,
          token,
        });
      const engineBuildId = get(
        createEngineBuildAndUpdateEngineJWTRightsResponse,
        'data.createEngineBuild.id'
      );
      if (!engineBuildId) {
        throw new Error('Could not create a new engine build');
      }

      const engineJWTRights = get(
        createEngineBuildAndUpdateEngineJWTRightsResponse,
        'data.updateEngine.jwtRights'
      );
      if (!engineJWTRights) {
        throw new Error("Could not update engine's JWT Rights");
      }

      window.open(
        `${automatePageUri}/flow/${engineId}/${engineBuildId}`,
        '_blank',
        'noopener'
      );
    } catch (err2) {
      console.log('Error trying to create an engine build', err2);
    }
  }
}

function* getEngine(token, endpoint, engineId) {
  const query = builds.getEngineQuery;
  try {
    const engineResponse = yield fetchGraphQLApi({
      endpoint,
      query,
      variables: { engineId },
      operationName: null,
      token,
    });

    const engine = get(engineResponse, 'data.engine');
    if (!engine) {
      throw new Error("Could not get the engine's data");
    }
    return engine;
  } catch (err) {
    console.log("Error retrieving engine's data", err);
  }
}

function* reloadEngineBuildsPage(action) {
  const { engineId } = action.meta;
  yield put(builds.listBuilds(engineId, { limit: 10 }, { force: true }));
}

function* watchFetchBuildReport() {
  yield takeLatest(builds.DOWNLOAD_BUILD_REPORT, (action) =>
    downloadBuildReport(action.payload)
  );
}

function* watchBuildNodeRedEngine() {
  yield takeLatest(builds.BUILD_NODE_RED_ENGINE, buildNodeRedEngine);
}

function* watchFetchNodeRedFlowSuccess() {
  yield takeLatest(
    builds.FETCH_WORKFLOW_RUNTIME_DATA_SUCCESS,
    updateWorkflowData
  );
}

function* watchFetchNodeRedFlowFailure() {
  yield takeLatest(
    builds.FETCH_WORKFLOW_RUNTIME_DATA_FAILURE,
    showNotificationError
  );
}

function* watchUpdateWorkflowDataSuccess() {
  yield takeLatest(builds.UPDATE_WORLFLOW_DATA_SUCCESS, openWorkflowApp);
}

function* watchUpdateWorkflowDataFailure() {
  yield takeLatest(builds.UPDATE_WORLFLOW_DATA_FAILURE, showNotificationError);
}

function* watchRedirectToAutomateBuild() {
  yield takeLatest(
    builds.REDIRECT_LATEST_AUTOMATE_BUILD,
    redirectToLatestAutomateBuild
  );
}

function* watchCreateNewEngineBuildSuccess() {
  yield takeLatest(
    builds.CREATE_NEW_ENGINE_BUILD_SUCCESS,
    reloadEngineBuildsPage
  );
}

function* watchCreateNewEngineBuildFailure() {
  yield takeLatest(
    builds.CREATE_NEW_ENGINE_BUILD_FAILURE,
    showNotificationError
  );
}

export default function* root() {
  yield all([
    fork(watchFetchBuildReport),
    fork(watchBuildNodeRedEngine),
    fork(watchFetchNodeRedFlowSuccess),
    fork(watchFetchNodeRedFlowFailure),
    fork(watchUpdateWorkflowDataSuccess),
    fork(watchUpdateWorkflowDataFailure),
    fork(watchRedirectToAutomateBuild),
    fork(watchCreateNewEngineBuildSuccess),
    fork(watchCreateNewEngineBuildFailure),
  ]);
}



// WEBPACK FOOTER //
// ./src/client/modules/engine-builds/sagas.js